import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Braces, MonitorPlay, MessageSquareCode, GitCompare, ArrowRight, CheckCircle2, XCircle, AlertTriangle, Circle } from "lucide-react";
import axios from "axios";

const TOOL_ICONS = {
  "API Test Generator": Braces,
  "UI Test Recorder": MonitorPlay,
  "Prompt Validator": MessageSquareCode,
  "Regression Analyzer": GitCompare,
};

const TOOL_SPANS = [
  "md:col-span-5 lg:col-span-7",
  "md:col-span-3 lg:col-span-5",
  "md:col-span-3 lg:col-span-5",
  "md:col-span-5 lg:col-span-7",
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const ApiSimulation = () => (
  <div className="mt-5 bg-arena-base border border-white/[0.06] rounded-md p-4 font-mono text-xs overflow-hidden">
    <div className="text-zinc-600 mb-2"># Generated from OpenAPI spec</div>
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-3 h-3 text-arena-green shrink-0" />
        <span className="text-zinc-400">GET /api/users <span className="text-zinc-600">→ 200 OK</span> <span className="text-arena-green">PASS</span></span>
      </div>
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-3 h-3 text-arena-green shrink-0" />
        <span className="text-zinc-400">POST /api/users <span className="text-zinc-600">→ 201</span> <span className="text-arena-green">PASS</span></span>
      </div>
      <div className="flex items-center gap-2">
        <XCircle className="w-3 h-3 text-red-400 shrink-0" />
        <span className="text-zinc-400">DELETE /api/users/:id <span className="text-zinc-600">→ 500</span> <span className="text-red-400">FAIL</span></span>
      </div>
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
        <span className="text-zinc-400">PUT /api/users/:id <span className="text-zinc-600">→ no auth</span> <span className="text-amber-400">WARN</span></span>
      </div>
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-3 h-3 text-arena-green shrink-0" />
        <span className="text-zinc-400">GET /api/users/:id <span className="text-zinc-600">→ 200</span> <span className="text-arena-green">PASS</span></span>
      </div>
    </div>
    <div className="mt-3 pt-2 border-t border-white/[0.04] text-zinc-600">
      5 tests generated · 3 passed · 1 failed · 1 warning
    </div>
  </div>
);

const UiSimulation = () => (
  <div className="mt-5 bg-arena-base border border-white/[0.06] rounded-md p-4 font-mono text-xs overflow-hidden">
    <div className="flex items-center gap-2 mb-3">
      <Circle className="w-2 h-2 fill-red-400 text-red-400" />
      <span className="text-zinc-600">Recording...</span>
    </div>
    <div className="space-y-1.5 text-zinc-500">
      <div><span className="text-violet-400">click</span> → <span className="text-zinc-400">#login-btn</span></div>
      <div><span className="text-violet-400">type</span> → <span className="text-zinc-400">input[name="email"]</span> <span className="text-zinc-600">"user@test.com"</span></div>
      <div><span className="text-violet-400">type</span> → <span className="text-zinc-400">input[name="pass"]</span> <span className="text-zinc-600">"••••••"</span></div>
      <div><span className="text-violet-400">click</span> → <span className="text-zinc-400">button[type="submit"]</span></div>
      <div><span className="text-arena-blue">assert</span> → <span className="text-zinc-400">url contains "/dashboard"</span></div>
      <div><span className="text-arena-blue">assert</span> → <span className="text-zinc-400">h1.text === "Welcome"</span></div>
    </div>
    <div className="mt-3 pt-2 border-t border-white/[0.04] text-zinc-600">
      6 actions recorded · 2 assertions added
    </div>
  </div>
);

const PromptSimulation = () => (
  <div className="mt-5 bg-arena-base border border-white/[0.06] rounded-md p-4 font-mono text-xs overflow-hidden">
    <div className="text-zinc-600 mb-2"># Prompt stress test results</div>
    <div className="space-y-2">
      <div>
        <div className="flex justify-between mb-1">
          <span className="text-zinc-400">Consistency</span>
          <span className="text-arena-green">94%</span>
        </div>
        <div className="w-full h-1 bg-white/[0.06] rounded-full">
          <div className="h-full rounded-full bg-emerald-500" style={{ width: "94%" }} />
        </div>
      </div>
      <div>
        <div className="flex justify-between mb-1">
          <span className="text-zinc-400">Safety</span>
          <span className="text-arena-green">98%</span>
        </div>
        <div className="w-full h-1 bg-white/[0.06] rounded-full">
          <div className="h-full rounded-full bg-emerald-500" style={{ width: "98%" }} />
        </div>
      </div>
      <div>
        <div className="flex justify-between mb-1">
          <span className="text-zinc-400">Edge Cases</span>
          <span className="text-amber-400">72%</span>
        </div>
        <div className="w-full h-1 bg-white/[0.06] rounded-full">
          <div className="h-full rounded-full bg-amber-400" style={{ width: "72%" }} />
        </div>
      </div>
    </div>
    <div className="mt-3 pt-2 border-t border-white/[0.04] text-zinc-600">
      150 variants tested · 2 failures detected
    </div>
  </div>
);

const RegressionSimulation = () => (
  <div className="mt-5 bg-arena-base border border-white/[0.06] rounded-md p-4 font-mono text-xs overflow-hidden">
    <div className="text-zinc-600 mb-2"># Build #247 vs #246 diff analysis</div>
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <XCircle className="w-3 h-3 text-red-400 shrink-0" />
        <span className="text-zinc-400"><span className="text-red-400">REGRESSION</span> checkout flow — total broke after cart refactor</span>
      </div>
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
        <span className="text-zinc-400"><span className="text-amber-400">RISK</span> payment API — response schema changed (3 consumers)</span>
      </div>
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-3 h-3 text-arena-green shrink-0" />
        <span className="text-zinc-400"><span className="text-arena-green">OK</span> user auth — no impact detected</span>
      </div>
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-3 h-3 text-arena-green shrink-0" />
        <span className="text-zinc-400"><span className="text-arena-green">OK</span> dashboard — rendering unchanged</span>
      </div>
    </div>
    <div className="mt-3 pt-2 border-t border-white/[0.04] text-zinc-600">
      42 files changed · 1 regression · 1 risk · impact score: 7.2/10
    </div>
  </div>
);

const TOOL_SIMULATIONS = {
  "API Test Generator": ApiSimulation,
  "UI Test Recorder": UiSimulation,
  "Prompt Validator": PromptSimulation,
  "Regression Analyzer": RegressionSimulation,
};

export const QATools = ({ apiUrl }) => {
  const [tools, setTools] = useState([]);

  useEffect(() => {
    const fetchTools = async () => {
      try {
        const res = await axios.get(`${apiUrl}/tools`);
        setTools(res.data);
      } catch (err) {
        console.error("Failed to fetch tools:", err);
      }
    };
    fetchTools();
  }, [apiUrl]);

  return (
    <section
      id="tools"
      data-testid="qa-tools-section"
      className="relative py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <span className="text-xs font-mono text-violet-400 uppercase tracking-[0.2em]">
            Tooling
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mt-3 tracking-tight">
            Built-in QA Tools
          </h2>
          <p className="mt-4 text-sm font-mono text-zinc-500 max-w-lg">
            Everything you need to build, test, and validate your QA agents.
          </p>
        </motion.div>

        {/* Tetris Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-4"
        >
          {tools.map((tool, idx) => {
            const Icon = TOOL_ICONS[tool.name] || Braces;
            const span = TOOL_SPANS[idx] || "md:col-span-4 lg:col-span-6";
            const Simulation = TOOL_SIMULATIONS[tool.name];

            return (
              <motion.div
                key={tool.id}
                variants={itemVariants}
                data-testid={`tool-card-${tool.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`group relative p-8 bg-arena-surface border border-white/[0.06] rounded-md hover:border-white/[0.12] transition-colors ${span}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-arena-elevated border border-white/[0.08] rounded-md group-hover:border-violet-500/30 transition-colors">
                    <Icon className="w-5 h-5 text-zinc-400 group-hover:text-violet-400 transition-colors" />
                  </div>
                  {tool.status === "beta" && (
                    <span className="text-[10px] font-mono text-amber-400 uppercase tracking-wider px-2 py-0.5 border border-amber-500/20 rounded-full bg-amber-500/5">
                      Beta
                    </span>
                  )}
                </div>

                <h3 className="font-heading font-semibold text-white text-lg mb-2">
                  {tool.name}
                </h3>
                <p className="text-sm font-mono text-zinc-500 leading-relaxed">
                  {tool.description}
                </p>

                {Simulation && <Simulation />}

                <div className="mt-5 flex items-center gap-1 text-xs font-mono text-zinc-600 group-hover:text-zinc-400 transition-colors">
                  <span>Learn more</span>
                  <ArrowRight className="w-3 h-3" />
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
